import { QueryClient } from '@tanstack/react-query';
import { createFailureAwareQueryClient } from './apiFailureDetection';
import { initializeCacheWarming, CacheWarmingConfig } from './cacheWarmingService';
import { initializeCachePruning, CachePruningConfig } from './cachePruningService';
import { performanceMonitor } from './performanceMonitor';

export interface FallbackOptimizationConfig {
  cacheWarming?: Partial<CacheWarmingConfig>;
  cachePruning?: Partial<CachePruningConfig>;
  enablePerformanceMonitoring?: boolean;
  criticalCampaigns?: string[];
}

export class FallbackOptimizationManager {
  private queryClient: QueryClient;
  private cacheWarmingService: any;
  private cachePruningService: any;
  private isInitialized = false;

  constructor(config: FallbackOptimizationConfig = {}) {
    // Create failure-aware query client
    this.queryClient = createFailureAwareQueryClient();

    // Initialize services
    this.cacheWarmingService = initializeCacheWarming(this.queryClient, {
      criticalCampaigns: config.criticalCampaigns || [],
      prefetchOnStartup: true,
      backgroundRefreshInterval: 5 * 60 * 1000, // 5 minutes
      maxConcurrentWarmups: 3,
      ...config.cacheWarming,
    });

    this.cachePruningService = initializeCachePruning(this.queryClient, {
      maxCacheSize: 500,
      maxCacheAge: 24 * 60 * 60 * 1000, // 24 hours
      pruningInterval: 30 * 60 * 1000, // 30 minutes
      keepTopN: 100,
      criticalQueryKeys: ['tracking', 'campaigns'],
      ...config.cachePruning,
    });

    if (config.enablePerformanceMonitoring !== false) {
      this.setupPerformanceMonitoring();
    }
  }

  public async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    console.info('Initializing fallback optimization system...');

    try {
      // Start background services
      this.cacheWarmingService.startBackgroundRefresh();
      this.cachePruningService.startAutoPruning();

      // Warm critical data
      await this.cacheWarmingService.warmCriticalData();

      this.isInitialized = true;
      console.info('Fallback optimization system initialized successfully');
    } catch (error) {
      console.error('Failed to initialize fallback optimization system:', error);
      throw error;
    }
  }

  public getQueryClient(): QueryClient {
    return this.queryClient;
  }

  public addCriticalCampaign(campaignId: string): void {
    this.cacheWarmingService.addCriticalCampaign(campaignId);
    this.cachePruningService.addCriticalQueryKey(`campaigns:${campaignId}`);
  }

  public removeCriticalCampaign(campaignId: string): void {
    this.cacheWarmingService.removeCriticalCampaign(campaignId);
    this.cachePruningService.removeCriticalQueryKey(`campaigns:${campaignId}`);
  }

  public async performMaintenance(): Promise<{
    pruningResult: any;
    warmupStatus: any;
    performanceMetrics: any;
  }> {
    console.info('Performing cache maintenance...');

    const [pruningResult, warmupStatus] = await Promise.all([
      this.cachePruningService.performPruning(),
      Promise.resolve(this.cacheWarmingService.getWarmupStatus()),
    ]);

    const performanceMetrics = performanceMonitor.getMetrics();

    return {
      pruningResult,
      warmupStatus,
      performanceMetrics,
    };
  }

  public getSystemStatus(): {
    isInitialized: boolean;
    cacheSize: any;
    warmupStatus: any;
    performanceScore: number;
    recommendations: string[];
  } {
    const cacheSize = this.cachePruningService.getCacheSizeInfo();
    const warmupStatus = this.cacheWarmingService.getWarmupStatus();
    const efficiencyReport = performanceMonitor.getCacheEfficiencyReport();

    return {
      isInitialized: this.isInitialized,
      cacheSize,
      warmupStatus,
      performanceScore: efficiencyReport.overallEfficiency,
      recommendations: [
        ...efficiencyReport.recommendations,
        ...efficiencyReport.criticalIssues,
      ],
    };
  }

  public async emergencyOptimization(): Promise<void> {
    console.warn('Performing emergency optimization...');

    try {
      // Emergency pruning
      await this.cachePruningService.emergencyPruning();

      // Clear performance metrics to start fresh
      performanceMonitor.clearMetrics();

      // Re-warm only the most critical data
      const criticalCampaigns = this.cacheWarmingService.getCriticalCampaigns().slice(0, 5);
      await Promise.all(
        criticalCampaigns.map((campaignId: string) => 
          this.cacheWarmingService.warmCampaignData(campaignId, 'high')
        )
      );

      console.info('Emergency optimization completed');
    } catch (error) {
      console.error('Emergency optimization failed:', error);
      throw error;
    }
  }

  public exportDiagnostics(): string {
    const systemStatus = this.getSystemStatus();
    const performanceMetrics = performanceMonitor.exportMetrics();

    return JSON.stringify({
      timestamp: new Date().toISOString(),
      systemStatus,
      performanceMetrics: JSON.parse(performanceMetrics),
    }, null, 2);
  }

  public cleanup(): void {
    if (this.cacheWarmingService) {
      this.cacheWarmingService.cleanup();
    }
    if (this.cachePruningService) {
      this.cachePruningService.cleanup();
    }
    this.isInitialized = false;
  }

  private setupPerformanceMonitoring(): void {
    // Monitor query client events
    this.queryClient.getQueryCache().subscribe((event) => {
      if (event.type === 'updated' && event.query.state.data) {
        const queryKey = event.query.queryKey.join(':');
        const responseTime = Date.now() - (event.query.state.dataUpdatedAt || Date.now());
        
        performanceMonitor.recordRequest({
          timestamp: Date.now(),
          queryKey,
          responseTime: Math.max(responseTime, 0),
          source: 'api',
          success: !event.query.state.error,
          endpoint: this.extractEndpointFromQueryKey(event.query.queryKey),
        });
      }
    });
  }

  private extractEndpointFromQueryKey(queryKey: any[]): string {
    if (queryKey.length > 0) {
      const firstKey = String(queryKey[0]);
      if (firstKey.includes('tracking')) return '/api/tracking';
      if (firstKey.includes('campaigns')) return '/api/campaigns';
      if (firstKey.includes('analytics')) return '/api/analytics';
    }
    return '/api/unknown';
  }
}

// Global instance
let globalOptimizationManager: FallbackOptimizationManager | null = null;

export const initializeFallbackOptimization = (
  config?: FallbackOptimizationConfig
): FallbackOptimizationManager => {
  if (globalOptimizationManager) {
    globalOptimizationManager.cleanup();
  }

  globalOptimizationManager = new FallbackOptimizationManager(config);
  return globalOptimizationManager;
};

export const getFallbackOptimizationManager = (): FallbackOptimizationManager | null => {
  return globalOptimizationManager;
};

// React hook
export const useFallbackOptimization = () => {
  return globalOptimizationManager;
};